import { useEffect, useMemo, useState } from "react";
import { Button, Select, Space, Typography } from "antd";
import { useTranslation } from "react-i18next";

import {
  DocumentDateFilterField,
  MatchingStatusFilter,
} from "../../../api/graphql/schema.generated";
import DateFiltersPicker, { DateValues } from "../../common/DateFiltersPicker";
import GroupedFilterDropdown, {
  GroupedFilterEntry,
} from "../../common/GroupedFilterDropdown";
import { getBerlinMomentTimezone } from "../../common/helpers";
import { DateFiltersWrapper } from "../../common/styledComponents";
import type { ReceiptFilterValues } from "./ReceiptTable";
import {
  INTEGRATION_GROUP_OPTIONS,
  ReceiptSourceFacets,
  STANDALONE_SOURCE_OPTIONS,
  SourceFilterOption,
  buildSourceFilterOptions,
  toFilterValues,
  toSelectedKeys,
} from "./sourceFilterOptions";

const { Text } = Typography;

// Sentinel for the "All" option (clears the matching-status filter).
const MATCHING_STATUS_ALL = "ALL";

/**
 * The date field implied by the pairs the page currently supplies, or undefined
 * when neither pair is set.
 *
 * SMT-1306: Rechnungsdatum only when an invoice-date range is actually set,
 * which is what keeps shared invoice-date links opening as they always did.
 * The undefined case is the caller's to interpret, and the two callers want
 * different things: the initial value falls back to the Erstellt am default,
 * while the re-sync leaves the operator's own choice alone.
 */
const deriveDateField = (
  filters: ReceiptFilterValues
): DocumentDateFilterField | undefined => {
  if (filters.dateFrom || filters.dateTo) {
    return DocumentDateFilterField.INVOICE_DATE;
  }
  if (filters.uploadDateFrom || filters.uploadDateTo) {
    return DocumentDateFilterField.CREATED_AT;
  }
  return undefined;
};

interface Props {
  filters: ReceiptFilterValues;
  onChange: (next: ReceiptFilterValues) => void;
  onReset: () => void;
  /**
   * SMT-1238: the sources this customer actually has receipts from. Undefined
   * (query not yet run, or errored — see index.tsx) falls back to the full
   * static list, today's behaviour.
   */
  sourceFacets?: ReceiptSourceFacets | null;
  /** SMT-1238: disables the source dropdown while the facets are loading, so
   * the operator can't pick from a list that is about to be replaced. */
  sourceFacetsLoading?: boolean;
}

/**
 * Horizontal filter bar above the ReceiptTable (SMT-905, OQ-3: horizontal
 * layout). SMT-994 redesigns it to match the rest of the Backoffice:
 *
 *  - Date-field selector (Rechnungsdatum / Erstellt am) — like BelegeView.
 *    SMT-1306: Erstellt am is the default, so receipts without an extracted
 *    invoice date are in scope on a fresh load.
 *  - One reused DateFiltersPicker (year / quarter / month / range); its output
 *    is routed to the invoice-date pair (dateFrom/dateTo) or the created-date
 *    pair (uploadDateFrom/uploadDateTo) depending on the selected field.
 *  - Source as a DropdownMultiSelect (like the Transaction category filter).
 *  - Matching status as a Select (All / Matched / Unmatched).
 *  - Reset button.
 *
 * The backend (EmailDocumentsListOptions) already exposes both date pairs, so
 * this is a presentational change only — no query/contract change.
 */
export const FilterPanel = ({
  filters,
  onChange,
  onReset,
  sourceFacets,
  sourceFacetsLoading,
}: Props) => {
  const { t } = useTranslation("ReceiptOverview");

  // The active date field is derived from which date pair is currently set, so
  // it is restored on reload (the pairs are URL-synced). Local state covers the
  // window where the operator switched the field but has not picked a date yet.
  // SMT-1306: Erstellt am is the fallback for an empty selection, so dates
  // cleared (or a field switch before a new pick) land on the new default.
  const [dateField, setDateField] = useState<DocumentDateFilterField>(
    () => deriveDateField(filters) ?? DocumentDateFilterField.CREATED_AT
  );

  // The panel is not remounted when the customer changes (index.tsx bumps the
  // key only after an upload), so the initial useState value would survive a
  // switch and leave the selector disagreeing with the pairs the page now
  // supplies — the next pick would then write the wrong pair. Re-sync whenever
  // a date pair arrives from outside. An empty selection is deliberately not
  // handled here: that is the operator clearing the dates or switching the
  // field, where the local choice must stand.
  // Destructured so the dependency list is the four values themselves. The
  // parent rebuilds `filters` on every render, so depending on the object would
  // run this on every render instead of on an actual date change.
  const { dateFrom, dateTo, uploadDateFrom, uploadDateTo } = filters;
  useEffect(() => {
    const derived = deriveDateField({
      dateFrom,
      dateTo,
      uploadDateFrom,
      uploadDateTo,
    });
    if (derived) setDateField(derived);
  }, [dateFrom, dateTo, uploadDateFrom, uploadDateTo]);

  // DateFiltersPicker keeps its own year/quarter/range state, so we remount it
  // (via key) on reset and on field switch to clear those internals.
  const [pickerNonce, setPickerNonce] = useState(0);

  const isCreatedAt = dateField === DocumentDateFilterField.CREATED_AT;
  const activeDateFrom = isCreatedAt
    ? filters.uploadDateFrom
    : filters.dateFrom;
  const activeDateTo = isCreatedAt ? filters.uploadDateTo : filters.dateTo;

  const handleDateFieldChange = (value: DocumentDateFilterField) => {
    setDateField(value);
    setPickerNonce((n) => n + 1);
    // Clear both pairs so switching never leaves a stale range on the old field.
    onChange({
      ...filters,
      dateFrom: undefined,
      dateTo: undefined,
      uploadDateFrom: undefined,
      uploadDateTo: undefined,
    });
  };

  const handleDateChange = (values: DateValues) => {
    if (isCreatedAt) {
      onChange({
        ...filters,
        uploadDateFrom: values.dateFrom,
        uploadDateTo: values.dateTo,
        dateFrom: undefined,
        dateTo: undefined,
      });
    } else {
      onChange({
        ...filters,
        dateFrom: values.dateFrom,
        dateTo: values.dateTo,
        uploadDateFrom: undefined,
        uploadDateTo: undefined,
      });
    }
  };

  // SMT-1238: the per-customer option list built from the facets the backend
  // reports, when available. `undefined` otherwise — no facets yet, or the
  // facets query itself errored (index.tsx passes `sourceFacets={undefined}`
  // in that case) — so every consumer below falls back to the static
  // universe via `toSelectedKeys`/`toFilterValues`'s own default parameter,
  // which is today's (pre-SMT-1238) behaviour. A facet outage must never make
  // receipts unfilterable.
  //
  // Depends on the active `uploadSources`/`documentOrigins` as joined
  // primitive strings (as `ReceiptTable` does for the same two arrays)
  // rather than on the arrays themselves, which the parent rebuilds as a
  // fresh literal on every render.
  const uploadSourcesKey = (filters.uploadSources ?? []).join("|");
  const documentOriginsKey = (filters.documentOrigins ?? []).join("|");
  const dynamicSourceOptions = useMemo(() => {
    if (!sourceFacets) return undefined;
    return buildSourceFilterOptions({
      facets: sourceFacets,
      activeUploadSources: filters.uploadSources,
      activeDocumentOrigins: filters.documentOrigins,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceFacets, uploadSourcesKey, documentOriginsKey]);

  const dynamicSourceOptionsList = dynamicSourceOptions
    ? [...dynamicSourceOptions.standalone, ...dynamicSourceOptions.integration]
    : undefined;

  const handleSourcesChange = (selectedKeys: string[]) => {
    onChange({
      ...filters,
      ...toFilterValues(selectedKeys, dynamicSourceOptionsList),
    });
  };

  const handleMatchingStatusChange = (value: string) => {
    onChange({
      ...filters,
      matchingStatus:
        value === MATCHING_STATUS_ALL
          ? undefined
          : (value as MatchingStatusFilter),
    });
  };

  const handleReset = () => {
    // SMT-1306: back to the default field, matching the created-at year range
    // the page writes in its own onReset.
    setDateField(DocumentDateFilterField.CREATED_AT);
    setPickerNonce((n) => n + 1);
    onReset();
  };

  // Reset only makes sense when at least one filter is set — otherwise it
  // would be visual noise on the empty state.
  const hasActiveFilters = Boolean(
    filters.dateFrom ||
      filters.dateTo ||
      filters.uploadDateFrom ||
      filters.uploadDateTo ||
      filters.uploadSources?.length ||
      filters.documentOrigins?.length ||
      filters.matchingStatus
  );

  const sourceEntries = useMemo<GroupedFilterEntry[]>(() => {
    // Provider names are brand names and stay untranslated; everything else
    // resolves through the ReceiptOverview namespace.
    const optionLabel = (option: SourceFilterOption) =>
      option.labelKey ? t(option.labelKey) : option.label;

    // SMT-1238: once the facets are in, show only what this customer actually
    // has (plus whatever is already active — see buildSourceFilterOptions)
    // instead of the full static universe.
    if (dynamicSourceOptions) {
      const entries: GroupedFilterEntry[] = dynamicSourceOptions.standalone.map(
        (option) => ({
          kind: "option" as const,
          value: option.key,
          label: optionLabel(option),
        })
      );

      // Only show the Integration group when it actually has something in
      // it — an empty group would just be an indeterminate checkbox over
      // nothing.
      if (dynamicSourceOptions.integration.length > 0) {
        entries.push({
          kind: "group" as const,
          key: "integration",
          label: t("source.integration"),
          options: dynamicSourceOptions.integration.map((option) => ({
            value: option.key,
            label: optionLabel(option),
          })),
        });
      }

      return entries;
    }

    return [
      ...STANDALONE_SOURCE_OPTIONS.map((option) => ({
        kind: "option" as const,
        value: option.key,
        label: optionLabel(option),
      })),
      {
        kind: "group" as const,
        key: "integration",
        label: t("source.integration"),
        options: INTEGRATION_GROUP_OPTIONS.map((option) => ({
          value: option.key,
          label: optionLabel(option),
        })),
      },
    ];
  }, [t, dynamicSourceOptions]);

  const selectedSourceKeys = toSelectedKeys(
    filters.uploadSources,
    filters.documentOrigins,
    dynamicSourceOptionsList
  );

  return (
    <Space wrap align="start" size="large">
      {/* Date column: single header with field-type selector directly beneath
          (SMT-994 rework: dateFieldLabel bold header removed; mirrors BelegeView). */}
      <Space direction="vertical" size={4}>
        <Text strong>{t("filter.dateLabel")}</Text>
        <Select
          value={dateField}
          onChange={handleDateFieldChange}
          aria-label={t("filter.dateLabel") ?? undefined}
          style={{ width: 160 }}
          options={[
            {
              label: t("filter.dateFieldInvoice"),
              value: DocumentDateFilterField.INVOICE_DATE,
            },
            {
              label: t("filter.dateFieldCreated"),
              value: DocumentDateFilterField.CREATED_AT,
            },
          ]}
        />
        <DateFiltersWrapper>
          <DateFiltersPicker
            key={`${dateField}-${pickerNonce}`}
            dateFrom={activeDateFrom ?? ""}
            dateTo={activeDateTo ?? ""}
            maxDate={getBerlinMomentTimezone().format()}
            onChange={handleDateChange}
            defaultYear={
              activeDateFrom ? activeDateFrom.slice(0, 4) : undefined
            }
            yearAsDropdown
          />
        </DateFiltersWrapper>
      </Space>

      {/* Source multiselect */}
      <Space direction="vertical" size={4}>
        <Text strong>{t("filter.sourcesLabel")}</Text>
        <GroupedFilterDropdown
          // Remount when the active sources change externally (reset / URL /
          // customer switch) so the internal checkbox state can't drift stale.
          key={selectedSourceKeys.slice().sort().join("|")}
          id="receipt-source"
          placeholder={t("filter.sourcesPlaceholder") ?? undefined}
          formatSelectedCount={(count) =>
            t("filter.sourcesSelected", { count })
          }
          values={selectedSourceKeys}
          entries={sourceEntries}
          onChange={handleSourcesChange}
          // SMT-1238: shown when the facets are in but this customer has no
          // receipts at all (entries is then empty). Disabled while the
          // facets are still loading, so the operator can't pick from a list
          // that is about to be replaced by the real one.
          emptyText={t("filter.sourcesNone") ?? undefined}
          disabled={Boolean(sourceFacetsLoading)}
        />
      </Space>

      {/* Matching status (matched / unmatched / all) + Reset */}
      <Space direction="vertical" size={4}>
        <Text id="receipt-matching-status-label" strong>
          {t("filter.matchingStatusLabel")}
        </Text>
        <Select
          aria-labelledby="receipt-matching-status-label"
          value={filters.matchingStatus ?? MATCHING_STATUS_ALL}
          onChange={handleMatchingStatusChange}
          style={{ width: 160 }}
          options={[
            {
              label: t("filter.matchingStatusAll"),
              value: MATCHING_STATUS_ALL,
            },
            {
              label: t("filter.matchingStatusMatched"),
              value: MatchingStatusFilter.MATCHED,
            },
            {
              label: t("filter.matchingStatusUnmatched"),
              value: MatchingStatusFilter.UNMATCHED,
            },
          ]}
        />
        {hasActiveFilters && (
          <Button
            type="link"
            onClick={handleReset}
            style={{
              padding: 0,
              height: "fit-content",
              width: "fit-content",
            }}
          >
            {t("filter.resetLabel")}
          </Button>
        )}
      </Space>
    </Space>
  );
};
